import React, {
    useEffect,
    useState,
    useCallback,
    useContext,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import {SVGIcon} from '@homeaway/react-svg';
import {CLOSE__24, ALERT_WARNING__24} from '@homeaway/svg-defs';
import {readCookie, writeCookie, COOKIE_NAME} from '../../utils/cookieHelpers';
import {TrackingContext} from '@homeaway/react-event-tracking';
import {ButtonIcon} from '@homeaway/react-buttons';
import classNames from 'classnames';

const MSG_TYPE = 'banner';

// Styles
import './GlobalBanner.less';

// eslint-disable-next-line complexity
function GlobalBanner({
    globalMessages,
    expirationDays,
    labels,
    hasUserDismissed,
    onDismiss,
}) {
    const isControlled = typeof hasUserDismissed !== 'undefined';
    const [isBannerDisabled, setIsBannerDisabled] = useState(
        isControlled ? hasUserDismissed : true
    );
    const [closeComplete, setCloseComplete] = useState(false);
    const bannerRef = useRef(null);
    const wasPresented = useRef(false);
    const {trigger} = useContext(TrackingContext);

    // transitionend event handler to remove the banner from the DOM.
    const completeClose = useCallback(
        (e) => {
            // Child transition events trigger here as well so make sure this
            // event is for the right target and the banner is in the process
            // of closing.
            if (
                e.target === bannerRef.current &&
                isBannerDisabled &&
                wasPresented.current &&
                !closeComplete
            ) {
                wasPresented.current = false;
                if (bannerRef.current) {
                    bannerRef.current.removeEventListener(
                        'transitionend',
                        completeClose
                    );
                }
                setCloseComplete(true);
            }
        },
        [closeComplete, isBannerDisabled]
    );

    useEffect(() => {
        let banner;
        if (bannerRef.current) {
            banner = bannerRef.current;
            bannerRef.current.addEventListener('transitionend', completeClose);
        }
        return () =>
            banner &&
            banner.removeEventListener('transitionend', completeClose);
    }, [completeClose]);

    // Code to run on component mount. If user has previously seen the banner
    // and dismissed it, then disable the banner. Otherwise, trigger a "presented"
    // EDAP event and listen for the transitionend event that occurs when
    // banner is dismissed.
    useEffect(() => {
        if (globalMessages && globalMessages.banner) {
            const banner = globalMessages.banner;
            const bannerDisabled = isControlled
                ? hasUserDismissed
                : readCookie(COOKIE_NAME, banner.id) === 'true';

            if (!bannerDisabled) {
                setIsBannerDisabled(false);
                wasPresented.current = true;
                trigger('global_message.presented', {
                    message_id: banner.id,
                    message_severity: banner.severity,
                    message_type: MSG_TYPE,
                });
            } else {
                trigger('global_message.hidden', {
                    interaction_type: 'cookie',
                    message_id: banner.id,
                    message_severity: banner.severity,
                    message_type: MSG_TYPE,
                });
            }
        }

        // This is intended as a one time run upon component mount so no
        // dependencies specified.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The callback invoked when the user closes the banner.
    const disableBanner = useCallback(
        (evt) => {
            const banner = globalMessages.banner;
            if (expirationDays !== 0) {
                writeCookie(COOKIE_NAME, 'true', banner.id);
            }

            trigger('global_message.hidden', {
                // Ideally, interaction_type here would be more specific
                // (ie. "touch" or "keypress"), but that would require more
                // complexity around detection than is warranted, so this will
                // be recorded as a "click" due to the callback being used.
                interaction_type: evt.type,
                message_id: banner.id,
                message_severity: banner.severity,
                message_type: MSG_TYPE,
            });
            setIsBannerDisabled(true);
            onDismiss();
        },
        [globalMessages, expirationDays, trigger, onDismiss]
    );

    const onLinkClick = useCallback(() => {
        const banner = globalMessages.banner;
        trigger('global_message_inline_link.selected', {
            message_id: banner.id,
            message_severity: banner.severity,
            message_type: MSG_TYPE,
            target_url: banner.body.link.href,
        });
    }, [globalMessages, trigger]);

    if (
        (isBannerDisabled && !wasPresented.current) ||
        !globalMessages ||
        !globalMessages.banner
    ) {
        return null;
    }

    const {body, severity, title} = globalMessages.banner;
    const {close} = labels;
    const bannerClass = classNames(
        'GlobalBanner',
        'bg--color-action-darker',
        'fade',
        'text-inverse',
        {
            in: !isBannerDisabled,
        }
    );
    const isHighSeverity = severity === 'HIGH';
    const ariaLive = isHighSeverity ? 'assertive' : 'polite';
    const msg = body && body.text && body.text.value;
    const msgLink = body && body.link;

    return (
        <div
            aria-live={ariaLive}
            className={bannerClass}
            ref={bannerRef}
            role="alert"
        >
            {isHighSeverity && (
                <SVGIcon
                    className="GlobalBanner__svg"
                    markup={ALERT_WARNING__24}
                />
            )}
            <div className="GlobalBanner__body">
                {msg && (
                    <p className="GlobalBanner__body-text">
                        <span>
                            {title && title.value && (
                                <strong className="text-inverse">{`${title.value} `}</strong>
                            )}
                            {msg}
                        </span>
                        {msgLink &&
                            msgLink.href &&
                            msgLink.text &&
                            msgLink.text.value && (
                                <a
                                    className="GlobalBanner__body-link a--inverse"
                                    href={msgLink.href}
                                    onClick={onLinkClick}
                                    rel="noopener"
                                    target="_blank"
                                >
                                    <strong className="text-inverse">
                                        {msgLink.text.value}
                                    </strong>
                                </a>
                            )}
                    </p>
                )}
            </div>

            <ButtonIcon
                className="GlobalBanner__close"
                circle
                inverse
                label={close}
                noBorder
                onClick={disableBanner}
                size="sm"
                svgMarkup={CLOSE__24}
            />
        </div>
    );
}

GlobalBanner.propTypes = {
    /** Data retrieved from the global messaging service. */
    globalMessages: PropTypes.shape({
        /** The banner content. */
        banner: PropTypes.shape({
            /** Message body content. */
            body: PropTypes.shape({
                text: PropTypes.shape({
                    value: PropTypes.string,
                }),
                /** Inline link information. */
                link: PropTypes.shape({
                    href: PropTypes.string,
                    text: PropTypes.shape({
                        value: PropTypes.string,
                    }),
                }),
            }),
            /** Message ID */
            id: PropTypes.string,
            /** Message severity. 'HIGH' or 'LOW' */
            severity: PropTypes.string,
            /** The message title information. */
            title: PropTypes.shape({
                value: PropTypes.string,
            }),
        }),
    }),
    /** Boolean indicating that the component is controlled by its parent. If this property is not set, the dismissed state is controlled by the banner itself. */
    hasUserDismissed: PropTypes.bool,
    // Internal property for examples and documentation.
    // expirationDays: PropTypes.number,
    /** Object containing translations for the current locale. It is assumed that the parent app will prepare and provide the object matching the shape seen here. */
    labels: PropTypes.shape({
        close: PropTypes.string,
    }),
    /** A callback which runs when user dismisses the banner. */
    onDismiss: PropTypes.func,
};

GlobalBanner.defaultProps = {
    labels: {
        close: 'Close',
    },
    onDismiss: () => {},
};

export default GlobalBanner;
